/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Share Chat Session Modal (Task 10.3.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import { useChatStore } from '../../stores/useChatStore';
import { SYSTEM_CHAT_PERSONAS, CHAT_AI_MODELS } from '../../data/chat-personas.catalog';
import { useI18n } from '../../hooks/useI18n';
import { getWorkspaceDomains } from '../../lib/urls';
import {
  X,
  Share2,
  Copy,
  Check,
  Globe,
  Lock,
  Eye,
  MessageSquare,
  Sparkles,
  Bot,
} from 'lucide-react';

export const ShareChatModal: React.FC = () => {
  const { t } = useI18n();
  const {
    isShareModalOpen,
    shareModalSessionId,
    closeShareModal,
    sessions,
  } = useChatStore();

  const [copied, setCopied] = useState(false);
  const [isAnonymized, setIsAnonymized] = useState(true);
  const [includeSystemPrompt, setIncludeSystemPrompt] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isShareModalOpen) {
        closeShareModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isShareModalOpen, closeShareModal]);

  if (!isShareModalOpen) return null;

  const targetSession = sessions.find((s) => s.id === shareModalSessionId) || sessions[0];
  const targetPersona = SYSTEM_CHAT_PERSONAS.find((p) => p.id === targetSession?.personaId) || SYSTEM_CHAT_PERSONAS[0];
  const targetModel = CHAT_AI_MODELS.find((m) => m.code === targetSession?.modelCode) || CHAT_AI_MODELS[0];

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/share/chat/${targetSession?.id || 'demo'}${isAnonymized ? '?anon=1' : ''}`
    : `${getWorkspaceDomains().appOrigin}/share/chat/${targetSession?.id || 'demo'}`;

  const handleCopyLink = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Ignored
    }
  };

  const handleShareSocial = (platform: 'twitter' | 'linkedin' | 'telegram') => {
    const text = encodeURIComponent(
      t('chat.share.socialShareText', { name: targetPersona.name }),
    );
    const url = encodeURIComponent(shareUrl);

    let targetHref = '';
    if (platform === 'twitter') {
      targetHref = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    } else if (platform === 'linkedin') {
      targetHref = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    } else if (platform === 'telegram') {
      targetHref = `https://t.me/share/url?url=${url}&text=${text}`;
    }

    if (targetHref && typeof window !== 'undefined') {
      window.open(targetHref, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeShareModal}
      />

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Share2 className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground">
                {t('chat.shareTitle')}
              </h3>
              <p className="text-[11px] text-foreground-muted">
                {t('chat.shareSubtitle')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeShareModal}
            className="p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Live Preview Card */}
        <div className="p-3.5 rounded-2xl border border-surface-border bg-surface-elevated/50 space-y-2.5">
          <div className="flex items-center justify-between text-[11px] text-foreground-muted">
            <span className="flex items-center gap-1 font-mono text-primary font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Sofia Platform Share
            </span>
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3 text-emerald-500" />
              {t('chat.sharePublic')}
            </span>
          </div>

          <div className="space-y-1">
            <h4 className="font-heading font-bold text-xs text-foreground line-clamp-1">
              {targetSession?.title || t('chat.title')}
            </h4>
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-foreground-muted">
              <span className="flex items-center gap-1">
                <Bot className="w-3 h-3 text-primary" />
                {targetPersona.name} ({targetModel.name})
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {t('chat.messagesCount', { count: targetSession?.totalMessages || 0 })}
              </span>
            </div>
          </div>
        </div>

        {/* Privacy Controls */}
        <div className="space-y-2.5 text-xs">
          <label className="flex items-center justify-between p-2.5 rounded-xl border border-surface-border bg-surface hover:bg-muted/40 cursor-pointer transition-colors">
            <div className="flex items-center gap-2.5">
              <Lock className="w-4 h-4 text-primary" />
              <div>
                <span className="font-medium text-foreground block">{t('chat.anonymizeInfo')}</span>
                <span className="text-[10px] text-foreground-muted block">
                  {t('chat.anonymizeInfoDesc')}
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={isAnonymized}
              onChange={(e) => setIsAnonymized(e.target.checked)}
              className="w-4 h-4 rounded text-primary focus:ring-primary accent-primary cursor-pointer"
            />
          </label>

          <label className="flex items-center justify-between p-2.5 rounded-xl border border-surface-border bg-surface hover:bg-muted/40 cursor-pointer transition-colors">
            <div className="flex items-center gap-2.5">
              <Eye className="w-4 h-4 text-accent" />
              <div>
                <span className="font-medium text-foreground block">{t('chat.showSystemPrompt')}</span>
                <span className="text-[10px] text-foreground-muted block">
                  {t('chat.showSystemPromptDesc')}
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={includeSystemPrompt}
              onChange={(e) => setIncludeSystemPrompt(e.target.checked)}
              className="w-4 h-4 rounded text-primary focus:ring-primary accent-primary cursor-pointer"
            />
          </label>
        </div>

        {/* Public Share URL Box */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wider block">
            {t('chat.publicShareLink')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 px-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary truncate shadow-inner"
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-primary-fg hover:opacity-95 text-xs font-semibold shadow-sm transition-all"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>{t('chat.copiedShareLink')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>{t('chat.copyShareLink')}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Quick Social Share Buttons */}
        <div className="space-y-1.5 pt-1">
          <span className="text-[10px] uppercase font-bold text-foreground-muted tracking-wider block">
            {t('chat.socialShareHeading')}
          </span>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleShareSocial('twitter')}
              className="p-2 rounded-xl border border-surface-border hover:border-primary/40 bg-surface hover:bg-muted text-xs font-medium transition-all text-center"
            >
              Twitter / X
            </button>
            <button
              type="button"
              onClick={() => handleShareSocial('linkedin')}
              className="p-2 rounded-xl border border-surface-border hover:border-primary/40 bg-surface hover:bg-muted text-xs font-medium transition-all text-center"
            >
              LinkedIn
            </button>
            <button
              type="button"
              onClick={() => handleShareSocial('telegram')}
              className="p-2 rounded-xl border border-surface-border hover:border-primary/40 bg-surface hover:bg-muted text-xs font-medium transition-all text-center"
            >
              Telegram
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
